/**
 * The notification center: what is waiting on you, and what just happened.
 *
 * Built only from what the application already knows. "Waiting on you" is
 * the pending purchase approvals count the shell already fetches for an
 * approver; the activity below it is the same audit feed the dashboard shows,
 * in the same scope (useActivityScope) — your own, or everyone's for an
 * administrator who chose that — fetched when the panel is opened. There is
 * no separate notification store behind this, so nothing is marked read: it
 * is a window onto live state.
 */
import { useTranslation } from 'react-i18next';
import { Link } from '../routes/orgNavigation.jsx';
import { Icon } from '../components/Icon.jsx';
import { Popover } from '../components/Popover.jsx';
import { useApi } from '../hooks/useApi.js';
import { useActivityScope } from '../hooks/useActivityScope.js';
import { useTenant } from '../context/TenantContext.jsx';
import { platformApi } from '../services/index.js';
import { number, relativeTime } from '../lib/format.js';
import {
  activityActionLabel,
  activityLink,
  activityPageFor,
  activityTone,
  actorLabel,
  dayBucket,
  isPlatformActor,
} from '../lib/activity.js';

function ActivityList({ tenantId, scope, selfId }) {
  const { t } = useTranslation(['navigation', 'administration']);
  const feed = useApi(
    async (signal) => {
      const result = await platformApi.auditFeed(tenantId, { limit: 10, scope }, { signal });
      return result?.data ?? [];
    },
    [tenantId, scope],
    { initialData: [] }
  );

  if (feed.loading) {
    return (
      <div className="notif-skeleton" aria-hidden="true">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="skeleton" style={{ width: `${82 - row * 9}%` }} />
        ))}
      </div>
    );
  }

  if (feed.error) {
    return (
      <div className="notif-empty">
        <p className="text-sm muted">{t('notifications.feedFailed')}</p>
        <button type="button" className="btn btn-sm" onClick={feed.refetch}>
          {t('common:actions.retry')}
        </button>
      </div>
    );
  }

  if (!feed.data?.length) {
    return (
      <div className="notif-empty">
        <p className="text-sm muted">
          {scope === 'all' ? t('notifications.emptyAll') : t('notifications.emptyMine')}
        </p>
      </div>
    );
  }

  let lastBucket = null;
  return (
    <div className="notif-feed">
      {feed.data.map((event) => {
        const bucket = dayBucket(event.createdAt);
        const heading = bucket !== lastBucket ? bucket : null;
        lastBucket = bucket;
        const to = activityLink(event);
        const body = (
          <>
            <span className={`notif-dot notif-dot-${activityTone(event.action) || 'neutral'}`} aria-hidden="true" />
            <span className="notif-text">
              <span className="notif-title">{activityActionLabel(event.action)}</span>
              <span className="notif-meta">
                {scope === 'all' && (
                  <>
                    {actorLabel(event, selfId)}
                    {isPlatformActor(event) && ` ${t('notifications.platform')}`} ·{' '}
                  </>
                )}
                {relativeTime(event.createdAt)}
              </span>
            </span>
          </>
        );
        return (
          <div key={event.id}>
            {heading && <div className="notif-day">{heading}</div>}
            {to ? (
              <Link to={to} className="notif-row notif-row-link">
                {body}
              </Link>
            ) : (
              <div className="notif-row">{body}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function NotificationCenter({ pendingApprovals = 0 }) {
  const { t } = useTranslation(['navigation', 'administration']);
  const { tenantId, can } = useTenant();
  const { scope, selfId, ready } = useActivityScope();
  const canApprove = can('purchase.approve');
  const waiting = canApprove && pendingApprovals > 0;

  return (
    <Popover
      label={t('notifications.title')}
      align="end"
      panelClassName="popover-notifications"
      trigger={({ triggerProps, open }) => (
        <button
          type="button"
          className={`topbar-icon-btn ${open ? 'is-open' : ''}`}
          {...triggerProps}
          aria-label={waiting ? t('notifications.triggerWaiting', { count: pendingApprovals }) : t('notifications.title')}
          title={t('notifications.title')}
        >
          <Icon name="bell" size={17} />
          {waiting && <span className="topbar-dot" aria-hidden="true" />}
        </button>
      )}
    >
      <div className="notif">
        <div className="popover-title">
          {t('notifications.title')}
          <Link to={activityPageFor(scope)} className="popover-title-link">
            {t('common:actions.viewAll')}
          </Link>
        </div>

        <div className="notif-section-label">{t('notifications.waitingOnYou')}</div>
        {waiting ? (
          <Link to="/purchase-requests?status=SUBMITTED" className="notif-attention">
            <span className="notif-attention-icon">
              <Icon name="approval" size={16} />
            </span>
            <span className="grow">
              <span className="notif-title">
                {t('notifications.pendingRequests', { count: pendingApprovals, n: number(pendingApprovals) })}
              </span>
              <span className="notif-meta">{t('notifications.reviewAndDecide')}</span>
            </span>
            <Icon name="chevron-right" size={15} className="subtle" />
          </Link>
        ) : (
          <div className="notif-caught-up">
            <Icon name="success" size={16} />
            <span>{t('notifications.caughtUp')}</span>
          </div>
        )}

        {ready && tenantId && (
          <>
            <div className="notif-section-label">{scope === 'all' ? t('notifications.recentAll') : t('notifications.recentMine')}</div>
            <ActivityList tenantId={tenantId} scope={scope} selfId={selfId} />
          </>
        )}
      </div>
    </Popover>
  );
}
